import { useEffect, useState } from "react";

// Rotating full-bleed hero. `slides` = [{ id, src, title, caption }].
// variant: "event" (bottom-left, per-slide caption) | "center" (centered heading/tagline).
export const CoverReel = ({
  slides = [],
  overline,
  heading,
  blurb,
  icon: Icon,
  variant = "event",
  height = "68vh",
}) => {
  const imgs = slides;
  const [i, setI] = useState(0);
  const centered = variant === "center";

  useEffect(() => {
    if (imgs.length < 2) return;
    const t = setInterval(() => setI((p) => (p + 1) % imgs.length), 5000);
    return () => clearInterval(t);
  }, [imgs.length]);

  if (imgs.length === 0) return null;

  return (
    <div data-testid="cover-reel" className="relative w-full overflow-hidden bg-black" style={{ height, minHeight: 420 }}>
      {imgs.map((s, idx) => (
        <img
          key={s.id}
          src={s.src}
          alt={s.title || ""}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[1200ms] ease-in-out"
          style={{ opacity: idx === i ? 1 : 0 }}
        />
      ))}
      <div className={`absolute inset-0 ${centered ? "bg-obsidian/55" : "bg-gradient-to-t from-obsidian via-obsidian/40 to-obsidian/70"}`} />

      <div className={`relative h-full max-w-7xl mx-auto px-6 lg:px-10 flex flex-col ${centered ? "items-center text-center justify-center" : "justify-end pb-14"}`}>
        {overline && (
          <div className={`overline-label mb-3 flex items-center gap-2 ${centered ? "text-neutral-300" : "text-amber-glow"}`}>
            {Icon && <Icon size={13} />} {overline}
          </div>
        )}
        <h1 className={`font-serif font-light ${centered ? "text-5xl sm:text-7xl lg:text-8xl leading-[0.9]" : "text-5xl lg:text-7xl"}`}>
          {heading}
        </h1>
        {blurb && <p className={`mt-4 text-neutral-300 ${centered ? "max-w-xl mx-auto" : "max-w-2xl"}`}>{blurb}</p>}

        {!centered && (
          <div key={imgs[i].id} className="mt-6 animate-fade-up">
            {imgs[i].title && <div className="font-serif text-2xl lg:text-3xl font-light text-white drop-shadow">{imgs[i].title}</div>}
            {imgs[i].caption && <div className="text-neutral-300 text-sm mt-1 max-w-xl">{imgs[i].caption}</div>}
          </div>
        )}

        {imgs.length > 1 && (
          <div className={`flex gap-2 mt-6 ${centered ? "justify-center" : ""}`}>
            {imgs.map((s, idx) => (
              <button
                key={s.id}
                data-testid={`reel-dot-${idx}`}
                onClick={() => setI(idx)}
                aria-label={`Show slide ${idx + 1}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${idx === i ? "w-8 bg-amber-glow" : "w-3 bg-white/30 hover:bg-white/60"}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
